import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Lightbulb, Stethoscope, Building2, Church, Megaphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import ProductCatalogManager from "@/components/ProductCatalogManager";

const KIND_META = {
  business: { Icon: Briefcase, color: "bg-orange" },
  venture: { Icon: Lightbulb, color: "bg-amber-500" },
  professional: { Icon: Stethoscope, color: "bg-teal" },
  ngo: { Icon: Building2, color: "bg-teal-deep" },
  church: { Icon: Church, color: "bg-violet-600" },
  community_leader: { Icon: Megaphone, color: "bg-rose-500" },
};

const ProfileCatalogPage = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (!user || !id) return;
    setLoading(true);
    api.get(`/profiles/${id}`)
      .then(({ data }) => {
        if (data.user_id !== user.id && user.role !== "admin") {
          setError(t.profileCatalog.notYours);
          setProfile(null);
        } else {
          setProfile(data);
        }
      })
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [id, user, navigate, t]);

  if (loading) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (error || !profile) {
    return (
      <section className="section bg-cream min-h-[60vh]">
        <div className="container-tight max-w-2xl text-center">
          <p className="text-red-600">{error || t.profileCatalog.notFound}</p>
          <Link to="/my-profiles" className="btn-teal-outline mt-6">
            <ArrowLeft size={14} /> {t.myProfiles.backToPanel}
          </Link>
        </div>
      </section>
    );
  }

  const meta = KIND_META[profile.kind] || KIND_META.business;
  const Icon = meta.Icon;

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight max-w-5xl">
        <Link to="/my-profiles" className="text-sm text-teal-soft hover:text-orange inline-flex items-center gap-1" data-testid="back-to-panel">
          <ArrowLeft size={14} /> {t.myProfiles.backToPanel}
        </Link>

        <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-6 flex items-center gap-4">
          <span className={`w-14 h-14 rounded-2xl ${meta.color} text-white flex items-center justify-center shrink-0`}>
            <Icon size={22} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-orange">{t.profileCatalog.eyebrow}</p>
            <h1 className="font-display text-2xl md:text-3xl text-teal-deep leading-tight truncate">{profile.name}</h1>
            <p className="text-xs text-teal-soft mt-1">
              {profile.kind.replace("_", " ")} · {profile.city}{profile.state ? `, ${profile.state}` : ""}
            </p>
          </div>
          <Link to={`/profiles/edit/${profile.id}`} className="btn-teal-outline" data-testid="edit-profile-from-catalog">
            {t.myProfiles.editBtn}
          </Link>
        </div>

        <ProductCatalogManager profileId={profile.id} />
      </div>
    </section>
  );
};

export default ProfileCatalogPage;
